import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import SeoHead from '../components/SeoHead';
import Modal from '../components/common/Modal';

// ============================================================================
// Animations & Layout Base
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;


const Page = styled.main`
  background: var(--brand-canvas);
  min-height: 100vh;
  padding: 0 0 3rem;
  overflow-x: hidden;
  width: 100%;
`;

const TwoColumnLayout = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 8rem 2rem 6rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    padding: 6rem 1.25rem 4rem;
    gap: 3rem;
  }
`;

const Hero = styled.section`
  animation: ${fadeIn} 0.45s ease both;
`;

const Eyebrow = styled.p`
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: #AAA;
  margin-bottom: 0.5rem;
  text-transform: lowercase;
`;

const HeroTitle = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 400;
  letter-spacing: -0.03em;
  color: var(--brand-text);
  line-height: 1.15;
  margin-bottom: 1rem;
`;

const HeroSub = styled.p`
  font-size: clamp(1rem, 2.5vw, 1.1rem);
  color: #666;
  line-height: 1.6;
  margin: 0 0 2rem;
`;

const HeroQuote = styled.blockquote`
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  font-style: italic;
  color: #555;
  border-left: 2px solid var(--brand-gold);
  padding-left: 1rem;
  margin-bottom: 1.25rem;
`;

const RightColumn = styled.div`
  animation: ${fadeIn} 0.55s 0.1s ease both;
  background: #FFF;
  border-radius: 1.5rem;
  border: 1.5px solid var(--brand-text);
  box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.1);
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const ActionButton = styled.button`
  background: var(--brand-text);
  color: #fff;
  border: none;
  border-radius: 0.85rem;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  width: 100%;
  margin-top: 1.5rem;
  transition: background 0.15s ease;

  &:hover { background: #2a2a2a; }
`;

const ForEnterprise: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleAction = () => {
    setIsModalOpen(true);
  };

  return (
    <Page>
      <SeoHead
        title="AI Employees for Law & Finance | prest0"
        description="Deploy a dedicated AI employee for law or finance — private infrastructure, deep workflow support, and go-live in weeks, not quarters."
        keywords="AI for law, finance, dedicated infrastructure, prest0"
        canonicalUrl="https://prest0.ai/for-enterprise"
      />


      <TwoColumnLayout>
        <div>
          <Hero>
            <Eyebrow>Law · Finance</Eyebrow>
            <HeroTitle>AI employees for your vertical</HeroTitle>
            <HeroSub>Deploy a dedicated AI employee for law or finance — private infrastructure, deep workflow support, and go-live in weeks, not quarters.</HeroSub>
            <HeroQuote>
              "Implementing prest0 firm-wide reduced our initial research and drafting time by 60%, allowing partners to focus on strategy."
            </HeroQuote>
          </Hero>
        </div>

        <RightColumn>
          <img src="/images/prest0-logo.webp" alt="prest0 logo" width="80" style={{marginBottom: '2rem'}} />
          <h2 style={{fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--brand-text)', fontFamily: 'Space Grotesk'}}>Ready to get started?</h2>
          <p style={{color: '#666', marginBottom: '1rem', fontSize: '0.95rem'}}>
            Experience how prest0 is changing the legal landscape for you.
          </p>
          <ActionButton onClick={handleAction}>
            Talk to sales
          </ActionButton>
        </RightColumn>
      </TwoColumnLayout>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Book a demo"
        size="xlarge"
        maxWidth="1100px"
        footer={null}
      >
        <div style={{ height: '600px', width: '100%' }}>
          <iframe
            title="Book a Demo"
            src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ2bVpuAMuVJh3Xikmu1Bi9AuBaaHxB5a-HeOlE8xm3l2ehjgrdvG5QQEqmrLDKsF1Vgbncg2BIK?gv=true"
            style={{ border: 'none', width: '100%', height: '100%' }}
          ></iframe>
        </div>
      </Modal>
    </Page>
  );
};

export default ForEnterprise;
